import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { TradeLogicExecutionJournalRecord } from '@/components/TradeLogic/TradeLogicExecutionJournalTrace.tsx';
import TradeLogicExecutionJournalCard from '@/components/TradeLogic/TradeLogicExecutionJournalCard.tsx';
import { EmptyState } from '@/components/UI';

interface TradeLogicV2AttachmentSummary {
    definitionId: number;
    version: number | null;
    label: string;
}

interface TradeLogicV2ExecutionJournalViewerProps {
    entryAttachment: TradeLogicV2AttachmentSummary | null;
    exitAttachment: TradeLogicV2AttachmentSummary | null;
    entryJournals: TradeLogicExecutionJournalRecord[];
    exitJournals: TradeLogicExecutionJournalRecord[];
    isEntryLoading: boolean;
    isExitLoading: boolean;
    hasEntryError: boolean;
    hasExitError: boolean;
}

type JournalPhaseFilter = 'all' | 'arbitration' | 'execution';
type JournalStatusFilter = 'all' | 'ready' | 'suppressed' | 'executed' | 'failed';

const phaseFilterOptions: { value: JournalPhaseFilter; label: string }[] = [
    { value: 'all', label: 'All phases' },
    { value: 'arbitration', label: 'Arbitration' },
    { value: 'execution', label: 'Execution' }
];

const statusFilterOptions: { value: JournalStatusFilter; label: string }[] = [
    { value: 'all', label: 'All statuses' },
    { value: 'ready', label: 'Ready' },
    { value: 'suppressed', label: 'Suppressed' },
    { value: 'executed', label: 'Executed' },
    { value: 'failed', label: 'Failed' }
];

const matchesPhaseFilter = (journal: TradeLogicExecutionJournalRecord, selectedPhase: JournalPhaseFilter): boolean => {
    if (selectedPhase === 'all') {
        return true;
    }

    return journal.row.phase === selectedPhase;
};

const matchesStatusFilter = (journal: TradeLogicExecutionJournalRecord, selectedStatus: JournalStatusFilter): boolean => {
    if (selectedStatus === 'all') {
        return true;
    }

    return journal.row.status === selectedStatus;
};

const parseJournalPhaseFilter = (value: string): JournalPhaseFilter => {
    if (value === 'arbitration' || value === 'execution') {
        return value;
    }

    return 'all';
};

const parseJournalStatusFilter = (value: string): JournalStatusFilter => {
    if (value === 'ready' || value === 'suppressed' || value === 'executed' || value === 'failed') {
        return value;
    }

    return 'all';
};

const renderJournalPanel = (params: {
    attachment: TradeLogicV2AttachmentSummary;
    journals: TradeLogicExecutionJournalRecord[];
    isLoading: boolean;
    hasError: boolean;
    selectedPhase: JournalPhaseFilter;
    selectedStatus: JournalStatusFilter;
}): React.ReactNode => {
    const filteredJournals = params.journals.filter(
        (journal) => matchesPhaseFilter(journal, params.selectedPhase) && matchesStatusFilter(journal, params.selectedStatus)
    );

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/60 p-3">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-medium text-zinc-100">{params.attachment.label}</div>
                    <div className="mt-1 text-xs text-zinc-500">
                        Definition #{params.attachment.definitionId} | Version{' '}
                        <span className="font-mono">{params.attachment.version ?? 'latest compiled'}</span>
                    </div>
                </div>
                <div className="rounded-full border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-300">
                    {filteredJournals.length}
                    {filteredJournals.length !== params.journals.length ? ` of ${params.journals.length}` : ''} rows
                </div>
            </div>

            {params.isLoading ? (
                <div className="mt-3 text-sm text-zinc-400">Loading recent execution journals…</div>
            ) : params.hasError ? (
                <div className="mt-3 text-sm text-rose-300">Failed to load recent execution journals.</div>
            ) : params.journals.length === 0 ? (
                <EmptyState className="mt-1" size="sm" title="No journal rows have been recorded yet." />
            ) : filteredJournals.length === 0 ? (
                <EmptyState className="mt-1" size="sm" title="No journal rows match the current filters." />
            ) : (
                <div className="mt-3 space-y-2">
                    {filteredJournals.map((journal) => (
                        <TradeLogicExecutionJournalCard journal={journal} key={journal.row.id} />
                    ))}
                </div>
            )}
        </div>
    );
};

const TradeLogicV2ExecutionJournalViewer: React.FC<TradeLogicV2ExecutionJournalViewerProps> = ({
    entryAttachment,
    exitAttachment,
    entryJournals,
    exitJournals,
    isEntryLoading,
    isExitLoading,
    hasEntryError,
    hasExitError
}) => {
    const [selectedPhase, setSelectedPhase] = useState<JournalPhaseFilter>('all');
    const [selectedStatus, setSelectedStatus] = useState<JournalStatusFilter>('all');

    if (!entryAttachment && !exitAttachment) {
        return null;
    }

    return (
        <div className="mt-4 border-t border-zinc-800 pt-4">
            <div className="mb-3">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="text-sm font-medium text-zinc-200">Recent Trade Logic V2 Activity</div>
                        <div className="mt-1 text-xs text-zinc-500">
                            These journal rows reflect the saved strategy attachments currently running in live systems. Unsaved form edits
                            are not included here.
                        </div>
                    </div>
                    <Link
                        className="inline-flex items-center rounded-md border border-zinc-700 bg-zinc-950/80 px-3 py-2 text-xs font-medium text-zinc-100 hover:border-zinc-500 hover:text-white"
                        to="/user/tradeLogicExecutionJournals"
                    >
                        Open global activity
                    </Link>
                </div>
            </div>
            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block">
                    <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-500">Phase filter</span>
                    <select
                        className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-sky-700 focus:outline-none"
                        value={selectedPhase}
                        onChange={(event) => setSelectedPhase(parseJournalPhaseFilter(event.target.value))}
                    >
                        {phaseFilterOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="block">
                    <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-zinc-500">Status filter</span>
                    <select
                        className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-sky-700 focus:outline-none"
                        value={selectedStatus}
                        onChange={(event) => setSelectedStatus(parseJournalStatusFilter(event.target.value))}
                    >
                        {statusFilterOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {entryAttachment &&
                    renderJournalPanel({
                        attachment: entryAttachment,
                        journals: entryJournals,
                        isLoading: isEntryLoading,
                        hasError: hasEntryError,
                        selectedPhase,
                        selectedStatus
                    })}
                {exitAttachment &&
                    renderJournalPanel({
                        attachment: exitAttachment,
                        journals: exitJournals,
                        isLoading: isExitLoading,
                        hasError: hasExitError,
                        selectedPhase,
                        selectedStatus
                    })}
            </div>
        </div>
    );
};

export default TradeLogicV2ExecutionJournalViewer;
